import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Input, Select, Space, Table, Typography } from 'antd';
import { getInventoryUnits } from '../../services/inventory';
import type { InventoryUnit } from '../../types/inventory';
import {
  HANDLING_MODE_HINT,
  HANDLING_MODE_LABEL,
  HandlingMode,
  ReceivingException,
  ReceivingPlanItemInput,
  ReceivingPlanSlot,
  RuleMode,
} from '../../types/returnInspection';
import { formatInventoryUnitLabel } from '../../utils/inventoryUnitLabel';
import { MODE_OUTCOME } from './ruleDisplay';

const { Text, Paragraph } = Typography;

/** 查一批库存单元当前生效的规则：客户用规则管理接口，仓库用仓库专用接口，由外层决定 */
export type FetchRuleHits = (unitIds: number[]) => Promise<Record<number, { source: 'unit' | 'project' | 'customer' | null; mode: RuleMode | null }>>;

const SOURCE_TEXT: Record<'unit' | 'project' | 'customer', string> = {
  unit: '库存单元自设',
  project: '项目级规则',
  customer: '通用规则',
};

const MODES: HandlingMode[] = ['inspect', 'aiu', 'normal', 'destroy'];
const ACKNOWLEDGE_MODES: HandlingMode[] = ['destroy', 'normal'];

interface ItemState {
  mode?: HandlingMode;
  unit?: number | null;
}

interface Props {
  exception: ReceivingException;
  projectId?: number;
  /** 预报行的库存单元显示文字：库存单元 ID → 文字（系统编码 + 内容物） */
  unitLabels: Record<number, string>;
  /** customer：客户本人选择；warehouse：仓库在客户线下确认后代录入（必须写备注） */
  actor: 'customer' | 'warehouse';
  fetchRuleHits: FetchRuleHits;
  submitting: boolean;
  onSubmit: (payload: { items: ReceivingPlanItemInput[]; acknowledged: boolean; note: string }) => void | Promise<void>;
  onCancel?: () => void;
}

/**
 * 为收货异常选择处理方案：对实收的每一项货品（预报行 + 预报外物品）各选一种处理方式。
 * 数量不由这里填，以异常记录里的实收清单快照为准；预报外物品选"正常入库/按规则初检"时需要指定一个散货库存单元。
 */
const ReceivingPlanEditor: React.FC<Props> = ({
  exception, projectId, unitLabels, actor, fetchRuleHits, submitting, onSubmit, onCancel,
}) => {
  const slots = exception.slots;
  const [state, setState] = useState<Record<string, ItemState>>(() => {
    const initial: Record<string, ItemState> = {};
    exception.plan.forEach(entry => {
      initial[entry.key] = { mode: entry.mode, unit: entry.source === 'extra' ? entry.inventory_unit : null };
    });
    return initial;
  });
  const [acknowledged, setAcknowledged] = useState(exception.plan_acknowledged);
  const [note, setNote] = useState('');
  const [units, setUnits] = useState<InventoryUnit[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [hits, setHits] = useState<Record<number, { source: 'unit' | 'project' | 'customer' | null; mode: RuleMode | null }>>({});

  const needsExtraUnits = slots.some(slot => slot.source === 'extra');

  // 预报外物品要指定库存单元时，列出本项目的散货库存单元
  useEffect(() => {
    if (!projectId || !needsExtraUnits) return undefined;
    let cancelled = false;
    (async () => {
      setUnitsLoading(true);
      try {
        const all: InventoryUnit[] = [];
        let page = 1;
        let hasMore = true;
        while (hasMore) {
          const res = await getInventoryUnits({ project: projectId, unit_type: 'LIU', page, page_size: 100 });
          all.push(...res.data.results);
          hasMore = !!res.data.next;
          page += 1;
        }
        if (!cancelled) setUnits(all);
      } catch {
        if (!cancelled) setUnits([]);
      } finally {
        if (!cancelled) setUnitsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, needsExtraUnits]);

  // 选了"按预设规则初检"的库存单元，实时显示它当前适用的规则
  const inspectUnitIds = useMemo(() => {
    const ids = new Set<number>();
    slots.forEach(slot => {
      const item = state[slot.key];
      if (item?.mode !== 'inspect') return;
      const unitId = slot.source === 'extra' ? item.unit : slot.inventory_unit;
      if (unitId) ids.add(unitId);
    });
    return Array.from(ids).sort((a, b) => a - b);
  }, [slots, state]);
  const inspectKey = inspectUnitIds.join(',');
  useEffect(() => {
    if (inspectUnitIds.length === 0) return undefined;
    let cancelled = false;
    fetchRuleHits(inspectUnitIds)
      .then(result => { if (!cancelled) setHits(prev => ({ ...prev, ...result })); })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectKey]);

  const unitOptions = units.map(unit => ({ value: unit.id, label: formatInventoryUnitLabel(unit).short }));

  const unitIdOf = (slot: ReceivingPlanSlot): number | null =>
    slot.source === 'extra' ? state[slot.key]?.unit ?? null : slot.inventory_unit;

  const ruleProblem = (slot: ReceivingPlanSlot): string | null => {
    if (state[slot.key]?.mode !== 'inspect') return null;
    const unitId = unitIdOf(slot);
    if (!unitId) return null;
    const hit = hits[unitId];
    if (hit === undefined) return null; // 还在查询
    return hit?.source ? null : '该库存单元当前没有生效的初检规则，不能选择"按预设规则初检"';
  };

  const complete = slots.every(slot => {
    const item = state[slot.key];
    if (!item?.mode) return false;
    if (slot.source === 'extra' && (item.mode === 'normal' || item.mode === 'inspect') && !item.unit) return false;
    return !ruleProblem(slot);
  });
  const needsAcknowledge = slots.some(slot => ACKNOWLEDGE_MODES.includes(state[slot.key]?.mode as HandlingMode));
  const noteOk = actor === 'customer' || note.trim().length > 0;
  const canSubmit = complete && (!needsAcknowledge || acknowledged) && noteOk;

  const patch = (key: string, next: Partial<ItemState>) => setState(prev => ({ ...prev, [key]: { ...prev[key], ...next } }));

  const handleSubmit = () => {
    const items: ReceivingPlanItemInput[] = slots.map(slot => ({
      key: slot.key,
      mode: state[slot.key].mode as HandlingMode,
      inventory_unit: slot.source === 'extra' ? state[slot.key].unit ?? null : undefined,
    }));
    return onSubmit({ items, acknowledged: needsAcknowledge ? acknowledged : false, note: note.trim() });
  };

  const renderRuleHint = (slot: ReceivingPlanSlot) => {
    const item = state[slot.key];
    if (item?.mode !== 'inspect') return null;
    const unitId = unitIdOf(slot);
    if (!unitId) return null;
    const hit = hits[unitId];
    if (hit === undefined) return <Text type="secondary" style={{ fontSize: 12 }}>查询规则中…</Text>;
    if (!hit?.source) return <Text type="danger" style={{ fontSize: 12 }}>{ruleProblem(slot)}</Text>;
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        当前适用规则：{SOURCE_TEXT[hit.source]}，{hit.mode ? MODE_OUTCOME[hit.mode] : '-'}
      </Text>
    );
  };

  return (
    <div>
      <Paragraph type="secondary" style={{ marginBottom: 8 }}>
        请对仓库实收的每一项货品选择处理方式。数量以仓库核对的实收清单为准，不需要填写。
        {actor === 'warehouse' ? '（你正在代客户录入，请务必在下方写明客户是如何确认的。）' : ''}
      </Paragraph>
      <Table
        size="small"
        pagination={false}
        rowKey="key"
        dataSource={slots}
        columns={[
          {
            title: '货品',
            key: 'item',
            width: 300,
            render: (_: unknown, slot: ReceivingPlanSlot) =>
              slot.source === 'forecast_line' ? (
                <div>
                  <Text strong>预报内</Text>
                  <div style={{ fontSize: 12 }}>{unitLabels[slot.inventory_unit as number] || slot.description}</div>
                </div>
              ) : (
                <div>
                  <Text strong style={{ color: '#cf1322' }}>预报外</Text>
                  <div style={{ fontSize: 12 }}>{slot.description}</div>
                </div>
              ),
          },
          { title: '实收数量', dataIndex: 'quantity', key: 'quantity', width: 90, align: 'center' as const },
          {
            title: '处理方式',
            key: 'mode',
            render: (_: unknown, slot: ReceivingPlanSlot) => (
              <div>
                <Select
                  style={{ width: 260 }}
                  placeholder="请选择处理方式"
                  value={state[slot.key]?.mode}
                  onChange={value => patch(slot.key, { mode: value })}
                  options={MODES.map(mode => ({ value: mode, label: HANDLING_MODE_LABEL[mode] }))}
                />
                {state[slot.key]?.mode && (
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>{HANDLING_MODE_HINT[state[slot.key].mode as HandlingMode]}</div>
                )}
                {slot.source === 'extra' && (state[slot.key]?.mode === 'normal' || state[slot.key]?.mode === 'inspect') && (
                  <Select
                    style={{ width: '100%', minWidth: 300, marginTop: 6 }}
                    showSearch
                    allowClear
                    loading={unitsLoading}
                    placeholder="指定对应的散货库存单元（可按条码、产品名称搜索）"
                    optionFilterProp="label"
                    value={state[slot.key]?.unit ?? undefined}
                    options={unitOptions}
                    onChange={value => patch(slot.key, { unit: value })}
                    notFoundContent={unitsLoading ? '加载中...' : '该项目下没有散货库存单元'}
                  />
                )}
                <div>{renderRuleHint(slot)}</div>
              </div>
            ),
          },
        ]}
      />
      {needsAcknowledge && (
        <Alert
          style={{ marginTop: 12 }}
          type="warning"
          showIcon
          message={
            <Checkbox checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)}>
              我已了解：选择"销毁"不可逆；选择"作为正常库存入库"不会做任何检查，货品入库后直接可售。
            </Checkbox>
          }
        />
      )}
      {actor === 'warehouse' && (
        <Input.TextArea
          style={{ marginTop: 12 }}
          rows={2}
          maxLength={500}
          placeholder="备注（必填）：客户是如何确认这个方案的，例如：客户已通过微信确认，2026-10-10"
          value={note}
          onChange={e => setNote(e.target.value)}
        />
      )}
      <Space style={{ marginTop: 12 }}>
        <Button type="primary" disabled={!canSubmit} loading={submitting} onClick={handleSubmit}>
          确认处理方案
        </Button>
        {onCancel && <Button onClick={onCancel}>取消</Button>}
        {!canSubmit && <Text type="secondary" style={{ fontSize: 12 }}>请为每一项选择处理方式{needsAcknowledge && !acknowledged ? '，并勾选"我已了解"' : ''}{actor === 'warehouse' && !note.trim() ? '，并填写备注' : ''}</Text>}
      </Space>
    </div>
  );
};

export default ReceivingPlanEditor;
